<template>
  <div class="page page-with-bg">
    <div class="page-form-wrap">
      <div class="page-form-header small-header">
        <h1>{{ survey.type === productType.AdvancedCRAAPOB ? 'Initial' : 'Follow-up' }} Advanced Cardiometabolic Risk Assessment Survey</h1>
      </div>
      <horizontal-stepper class="custom-stepper"
                          ref="stepper"
                          :steps="steps"
                          :displayNumeral="true">
      </horizontal-stepper>
      <div class="page-form-footer">
        <b-form-group class="form-buttons" :class="{'form-buttons-back': currentStep > 0}">
          <b-btn class="btn-back" variant="link text-secondary" @click="goBack" v-if="currentStep > 0">Back</b-btn>
          <span>
            <b-btn variant="success" @click="next" :disabled="loading">{{ buttonText }}</b-btn>
            <b-btn v-if="!survey.isCompleted && !reportToEditId" variant="link text-secondary" @click="save" :disabled="loading">Save to complete later</b-btn>
          </span>
          <template v-if="survey.isCompleted && reportToEditId">
            <b-btn variant="success" @click="completeSurvey" :disabled="loading">Submit</b-btn>
            <b-btn class="btn-back" variant="link text-secondary" @click="cancel">Cancel</b-btn>
          </template>
        </b-form-group>
      </div>
    </div>

    <b-modal ref="confirmSurveyCompletion"
             cancel-variant="outline-secondary"
             ok-variant="success"
             @ok="confirmSubmit"
             title="Confirm">
      <template v-if="survey.isCompleted && reportToEditId && this.survey.reportGenerated">
        This will regenerate the Cardiometabolic Risk Assessment report. Are you ready to submit your answers or do you want to go back to check?
      </template>
      <template v-else>
        This completes your Cardiometabolic Risk Assessment survey. Are you ready to submit your answers or do you want to go back to check?
      </template>
      <template #modal-footer="{ ok, cancel }">
        <b-button size="md" variant="light" @click="cancel()">
          Go Back
        </b-button>
        <b-button size="md" variant="success" @click="ok()">
          Submit
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
  import { createNamespacedHelpers } from 'vuex';
  const { mapActions: mapSurveyActions, mapState: mapSurveyState } = createNamespacedHelpers('survey');
  const { mapMutations: mapUserMutations, mapActions: mapUserActions } = createNamespacedHelpers('user');

  import genderType from 'enums/gender';
  import billingMethodType from 'enums/billing-method';
  import productType from 'enums/product-type';

  import { clientRole, clientUserRole, clientProviderRole, adminRole } from "@/constants";

  import { InitialAdvancedSurveySteps, RepeatedAdvancedSurveySteps } from './survey-steps';

  import jwtDecode from 'jwt-decode';

  export default {
    props: {
      type: {
        type: Number
      }
    },

    data() {
      return {
        currentStep: 0,
        steps: this.type === productType.AdvancedCRAAPOB ? InitialAdvancedSurveySteps : RepeatedAdvancedSurveySteps,
        genderType: genderType,
        productType: productType,
        buttonText: 'Continue',
        nextButtonText: "Continue",
        payButtonText: 'Pay Now',
        loading: false
      }
    },

    computed: {
      ...mapSurveyState(["survey"]),

      reportToEditId() {
        const userAuth = jwtDecode(this.$auth.tokenData.accessToken);

        return userAuth.report_to_edit_id;
      },

      isPatientInfoHidden() {
        const userAuth = jwtDecode(this.$auth.tokenData.accessToken);

        return Boolean(userAuth.patient_info_hidden);
      }
    },

    methods: {
      ...mapSurveyActions(["update"]),
      ...mapUserMutations(["setShowProfile"]),
      ...mapUserActions(["loginBackAsAdmin"]),

      findStepIndexById(id) {
        const object = this.steps.find(step => step.id === id);

        return this.steps.indexOf(object);
      },

      setStep(index) {
        this.$refs.stepper.setStep(index);
      },

      async save(pushRoute = true) {
        await this.update();

        if (!pushRoute)
          return;

        this.$root.successToast("Survey saved!");

        let userAuth = jwtDecode(this.$auth.tokenData.accessToken);

        let loggedInUser = JSON.parse(localStorage.getItem("loggedInUser"));

        if (loggedInUser && loggedInUser.roles.some(r => r === clientRole || r === clientUserRole || r === clientProviderRole || r === adminRole) && !userAuth.patient_info_hidden) {
          await this.loginBackAsAdmin();

          this.$router.push({ path: "/reports" });

          return;
        }

        if (userAuth.patient_info_hidden) {
          this.setShowProfile(false);
          await this.$auth.logout("/");

          return;
        }

        this.$router.push("survey-incompleted");
      },

      async next() {
        this.loading = true;

        await this.$refs.stepper.next();

        this.updateNextButtonText();

        if (this.$refs.stepper.finalStep)
          await this.completeSurvey();
        else
          await this.save(false);

        this.loading = false;
      },

      completeSurvey() {
        this.$refs.confirmSurveyCompletion.show();
      },

      async confirmSubmit() {
        this.loading = true;

        const regenerateReport = Boolean(this.survey.isCompleted && Boolean(this.reportToEditId) && this.survey.reportGenerated);

        this.survey.isCompleted = true;

        await this.update({ regenerateReport, cancel: false });
        this.$root.successToast("Survey completed!");

        let loggedInUser = JSON.parse(localStorage.getItem("loggedInUser"));
        let userAuth = jwtDecode(this.$auth.tokenData.accessToken);

        this.loading = false;

        if (userAuth.patient_info_hidden && !regenerateReport && !this.reportToEditId) {
          this.setShowProfile(false);
          await this.$auth.logout("/");

          return;
        }

        if (loggedInUser && loggedInUser.roles.some(r => r === clientRole || r === clientUserRole || r === clientProviderRole || r === adminRole) && (!userAuth.patient_info_hidden || regenerateReport || !this.survey.reportGenerated)) {
          await this.loginBackAsAdmin();

          this.$router.push({ path: "/reports" });

          return;
        }

        this.$router.push("survey-completed");
      },

      async cancel() {
        await this.update({ regenerateReport: false, cancel: true });

        await this.loginBackAsAdmin();

        this.$router.push({ path: "/reports" });

        return;
      },

      updateNextButtonText() {
        this.buttonText = this.$refs.stepper.currentStep.id === 'payment' ? this.payButtonText : this.nextButtonText;
      },

      goBack() {
        this.$refs.stepper.back();
        this.updateNextButtonText();
      }
    },

    watch: {
      'survey.bloodSugar.diabetesHistory.toldType1Diabetes': {
        immediate: true,
        handler(toldType1Diabetes) {
          this.steps[this.findStepIndexById('type-one-diabetes')].active = toldType1Diabetes;
        },
      },

      'survey.bloodSugar.diabetesHistory.toldType2Diabetes': {
        immediate: true,
        handler(toldType2Diabetes) {
          this.steps[this.findStepIndexById('type-two-diabetes')].active = toldType2Diabetes;
          this.steps[this.findStepIndexById('diabetes-type-two-remission')].active = toldType2Diabetes;
        }
      },

      'survey.gender': {
        immediate: true,
        handler(gender) {
          this.steps[this.findStepIndexById('pregnancy')].active = gender === genderType.Female;
        },
      },

      'survey.reportInvoice.billingMethod': {
        immediate: true,
        handler(method) {
          this.steps[this.findStepIndexById('payment')].active = method === billingMethodType.IndividualBilling;
        }
      }
    },

    mounted() {
      this.$watch(
        () => {
          this.currentStep = this.$refs.stepper?.currentStep?.index || 0;
          return this.$refs.stepper.currentStep;
        },
        (step) => {
          const stepIndex = step?.index || 0;
          this.currentStep = stepIndex;
          this.survey.lastStep = stepIndex;
        }
      );
    }
  }
</script>
<style lang="scss" scoped>
  @import '@/styles/mixins';

  .page {
    @include retina('/png/table-coffee.png');
  }
</style>